import { zodResolver } from "@hookform/resolvers/zod";
import { get } from "lodash";
import { FC } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { IconPicker } from "@/components/ui/icon-picker";
import { Input } from "@/components/ui/input";
import { useCMSTabContext } from "@/components/web-experience/cms-tab-context";
import { SKUPageContentGenericTabIcon, SKUPageContentTab } from "@/data/types";

import { icons } from "./data";

type Props = {
  options: SKUPageContentTab["options"];
  defaultTitle?: string;
  defaultIcon?: string;
};

const cmsTabDetailsFormSchema = z.object({
  title: z.string().max(15).optional(),
  icon: z.string().optional(),
});

export const TabDetails: FC<Props> = ({
  options,
  defaultTitle,
  defaultIcon,
}) => {
  const { onCustomize } = useCMSTabContext();

  const form = useForm({
    resolver: zodResolver(cmsTabDetailsFormSchema),
    defaultValues: {
      title: get(options, "tab_title", defaultTitle),
      icon: get(options, "tab_icon", defaultIcon),
    },
  });

  const onSubmit = (data: z.infer<typeof cmsTabDetailsFormSchema>) => {
    onCustomize(data.title, data.icon as SKUPageContentGenericTabIcon);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-medium leading-none">Tab</CardTitle>
        <CardDescription>
          Customize the tab icon and name below. Max of 15 characters
        </CardDescription>
      </CardHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="p-6 pt-0 flex flex-row gap-2 space-y-4">
            <FormField
              control={form.control}
              name="icon"
              render={({ field }) => (
                <FormItem className="flex">
                  <FormControl>
                    <IconPicker
                      icons={icons}
                      value={field.value as SKUPageContentGenericTabIcon}
                      onValueChange={field.onChange}
                      categorized={false}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="flex flex-grow">
                  <FormControl>
                    <Input
                      placeholder={defaultTitle}
                      maxLength={15}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </CardContent>

          <div className="border-t px-6 py-4">
            <Button type="submit">Save</Button>
          </div>
        </form>
      </Form>
    </Card>
  );
};
